import { DropdownItemNew, DropdownNew, LuneThemeNew, Text } from '@lune-fe/lune-ui-lib'
import AddIcon from '@mui/icons-material/Add'
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Divider from 'components/Divider'
import RFQModal from 'views/CalculateEmissions/RFQ/RFQModal'
import { NavigationSource } from 'views/CalculateEmissions/Shared/BackButtonWithFromState'
import FintechSheetUploadModal from 'views/CalculateEmissions/Shared/FintechSheetUploadModal'

const { spacing } = LuneThemeNew

interface NewCalculationDropdownProps {
    /**
     * Specifies the source view that opened the dropdown.
     * Used to determine the correct back navigation path when closing calculation forms.
     * When set to 'imports', the close button will navigate back to /imports instead of /calculate-emissions.
     */
    from?: NavigationSource
    /**
     * Callback invoked when an upload succeeds.
     * The parent component can decide what to do (navigate, reload, etc.)
     */
    onUploadSuccess?: (isSuccess: boolean) => void
}

const NewCalculationDropdown = ({ from, onUploadSuccess }: NewCalculationDropdownProps = {}) => {
    const navigate = useNavigate()
    const [fintechModalOpen, setFintechModalOpen] = useState(false)
    const [logisticsModalOpen, setLogisticsModalOpen] = useState(false)
    const [tenderModalOpen, setTenderModalOpen] = useState(false)

    const navigateWithState = (path: string) => {
        navigate(path, from ? { state: { from } } : undefined)
    }

    return (
        <>
            <DropdownNew
                buttonLabel={'New calculation'}
                leftIcon={<AddIcon />}
                paperSx={{
                    '.MuiButtonBase-root': {
                        height: spacing(6),
                    },
                }}
            >
                <Text
                    variant={'caption'}
                    sx={{
                        pl: 2,
                        lineHeight: spacing(4),
                        display: 'block',
                        fontWeight: 'medium',
                    }}
                >
                    Logistics
                </Text>
                <DropdownItemNew
                    leftIcon={<TableChartOutlinedIcon />}
                    onClick={() => setLogisticsModalOpen(true)}
                >
                    <Text variant={'body3'}>Spreadsheet calculation (.csv)</Text>
                </DropdownItemNew>
                <DropdownItemNew
                    leftIcon={<ListAltOutlinedIcon />}
                    onClick={() => setTenderModalOpen(true)}
                >
                    <Text variant={'body3'}>Tender calculation (.xls)</Text>
                </DropdownItemNew>
                <DropdownItemNew
                    leftIcon={<RouteOutlinedIcon />}
                    onClick={() => navigateWithState('/calculate-emissions/logistics-emissions')}
                >
                    <Text variant={'body3'}>Simple logistics calculation</Text>
                </DropdownItemNew>
                <Divider />
                <Text
                    variant={'caption'}
                    sx={{
                        mt: 1,
                        pl: 2,
                        lineHeight: spacing(4),
                        display: 'block',
                        fontWeight: 'medium',
                    }}
                >
                    Spend management
                </Text>
                <DropdownItemNew
                    leftIcon={<TableChartOutlinedIcon />}
                    onClick={() => setFintechModalOpen(true)}
                >
                    <Text variant={'body3'}>Spreadsheet calculation (.csv)</Text>
                </DropdownItemNew>
                <DropdownItemNew
                    leftIcon={<ShoppingCartOutlinedIcon />}
                    onClick={() => navigateWithState('/calculate-emissions/everyday-purchases')}
                >
                    <Text variant={'body3'}>Everyday Purchases</Text>
                </DropdownItemNew>
            </DropdownNew>
            <FintechSheetUploadModal
                open={fintechModalOpen}
                onClose={(isSuccess) => {
                    setFintechModalOpen(false)
                    if (isSuccess) {
                        if (onUploadSuccess) {
                            onUploadSuccess(true)
                        } else {
                            // Default behavior: navigate to imports if no callback provided
                            navigate('/imports')
                        }
                    }
                }}
            />
            <RFQModal
                from={from}
                sheetType="logistics_user_template"
                open={logisticsModalOpen}
                onClose={(isSuccess) => {
                    setLogisticsModalOpen(false)
                    if (isSuccess) {
                        if (onUploadSuccess) {
                            onUploadSuccess(true)
                        } else {
                            // Default behavior: navigate to imports if no callback provided
                            navigate('/imports')
                        }
                    }
                }}
            />
            <RFQModal
                from={from}
                sheetType="rfq"
                open={tenderModalOpen}
                onClose={(isSuccess) => {
                    setTenderModalOpen(false)
                    if (isSuccess) {
                        if (onUploadSuccess) {
                            onUploadSuccess(true)
                        } else {
                            // Default behavior: navigate to imports if no callback provided
                            navigate('/imports')
                        }
                    }
                }}
            />
        </>
    )
}

export default NewCalculationDropdown
